/**
 * Default `QueryClient` configuration for Mangrove POS apps (P2-21, ADR-0056 Layer 1).
 *
 * The defaults below balance freshness vs. server load for a POS workload:
 *
 *   - `staleTime: 30s`         — most catalog / inventory reads are cheap to
 *                                 re-fetch but rarely change between user
 *                                 actions; 30s mostly suppresses thundering
 *                                 herd from rapid re-renders.
 *   - `gcTime: 24h`             — keep cached queries in memory + IndexedDB for
 *                                 24h so a reload / a brief offline window
 *                                 doesn't lose the user's working set. The
 *                                 stale-while-revalidate render path returns
 *                                 cached data instantly, then refetches.
 *   - `networkMode: 'offlineFirst'` — render from cache + queue refetches/
 *                                 mutations when offline. TanStack Query
 *                                 auto-resumes paused mutations on `online`.
 *                                 Combined with the IndexedDB persister
 *                                 (`createOfflinePersister` in `@mangrove/ui`), this
 *                                 is the entire Layer-1 offline story.
 *   - `refetchOnWindowFocus`   — off. POS terminals never lose focus in normal
 *                                 use, and the admin app's sidebar nav causes
 *                                 spurious focus events on tab switches.
 *   - `refetchOnReconnect`     — on (TanStack default). Coming back online
 *                                 triggers a one-shot revalidation of stale
 *                                 queries, which is exactly what we want.
 *   - `retry`                  — only retry server-side (>= 500) failures, up
 *                                 to 2x. 4xx errors (validation, auth, not
 *                                 found, conflict) are deterministic; retrying
 *                                 just hides them.
 *   - `mutations.retry: false` — money-touching mutations carry idempotency
 *                                 keys at the API layer (P1-41 / ADR-0031), so
 *                                 retry is decided server-side. The client
 *                                 surfaces failures immediately. But mutations
 *                                 issued while offline are *paused* (not
 *                                 retried) and resumed on reconnect — that's
 *                                 the `networkMode` contract, not retry.
 */
import {
  QueryClient,
  type InvalidateOptions,
  type InvalidateQueryFilters,
  type QueryClientConfig,
} from '@tanstack/react-query';
import { TRPCClientError } from '@trpc/client';

const FOUR_HUNDRED = 400;
const FIVE_HUNDRED = 500;

/**
 * Returns false for tRPC errors with an HTTP status in the 4xx range — they
 * are deterministic and retrying just hides the bug.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof TRPCClientError) {
    const data = error.data as { httpStatus?: unknown } | null | undefined;
    const status = data?.httpStatus;
    if (typeof status === 'number' && status >= FOUR_HUNDRED && status < FIVE_HUNDRED) {
      return false;
    }
  }
  return failureCount < 2;
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export const defaultQueryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: ONE_DAY_MS,
      refetchOnWindowFocus: false,
      networkMode: 'offlineFirst',
      retry: shouldRetry,
    },
    mutations: {
      retry: false,
      networkMode: 'offlineFirst',
    },
  },
};

/** The key prefix tRPC v11 gives `auth.getCurrentUser`, the query every shell gates on. */
export const CURRENT_USER_QUERY_KEY = [['auth', 'getCurrentUser']] as const;

/** At most one session re-check in this window, however many calls fail at once. */
export const SESSION_RECHECK_MIN_GAP_MS = 5_000;

function isUnauthorized(error: unknown): boolean {
  if (!(error instanceof TRPCClientError)) return false;
  const data = error.data as { code?: unknown; httpStatus?: unknown } | null | undefined;
  return data?.code === 'UNAUTHORIZED' || data?.httpStatus === 401;
}

function isCurrentUserKey(key: readonly unknown[] | undefined): boolean {
  const path = key?.[0];
  return Array.isArray(path) && path[0] === 'auth' && path[1] === 'getCurrentUser';
}

/**
 * #791: a call refused for want of a session makes the app look at the session
 * again. Every shell sends a person to sign-in when `auth.getCurrentUser`
 * answers null, but a restored offline copy of it that is still fresh is not
 * refetched. So when an account was disabled, or its session ended elsewhere,
 * the open page stayed on screen with every call refused, for as long as
 * nothing else refetched it (walk-791-local, 30 Sep: not sent to sign-in
 * within 45 s). Refetching `getCurrentUser` settles it: it answers null, and
 * the shell's own redirect runs. The query itself is exempt (it never refuses,
 * it answers null), and bursts collapse into one re-check.
 */
function recheckSessionOnUnauthorized(client: QueryClient): QueryClient {
  // #803: the session's workspace can change OUTSIDE this app: prod shares
  // the sign-in cookie across *.mangroveitsoftware.com, so another app's
  // switcher (learn., app.) moves this tab's session too, without wiping this
  // app's cache. A restored getCurrentUser younger than the default 30 s
  // staleTime was then trusted, and a reload drew the previous workspace
  // (walk-803-two-tabs-local, 30 Sep: reproduced). With staleTime 0 the
  // restored copy still renders at once, and every mount asks the server.
  // The identity guard then sees the real workspace and resets the cache.
  // Observers that pass their own staleTime keep it.
  client.setQueryDefaults(CURRENT_USER_QUERY_KEY, { staleTime: 0 });
  let lastAt = Number.NEGATIVE_INFINITY;
  const recheck = (error: unknown, key: readonly unknown[] | undefined): void => {
    if (!isUnauthorized(error) || isCurrentUserKey(key)) return;
    const now = Date.now();
    if (now - lastAt < SESSION_RECHECK_MIN_GAP_MS) return;
    lastAt = now;
    void client.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });
  };
  client.getQueryCache().subscribe((event) => {
    if (event.type === 'updated' && event.action.type === 'error') {
      recheck(event.action.error, event.query.queryKey as readonly unknown[]);
    }
  });
  client.getMutationCache().subscribe((event) => {
    if (event.type === 'updated' && event.action.type === 'error') {
      recheck(event.action.error, undefined);
    }
  });
  return client;
}

/**
 * A refresh after a write sees the write, even when the first read is still in
 * flight (#839, and again on 9 Oct in RS Linkers UAT S08).
 *
 * TanStack's `invalidateQueries` cancels an in-flight read only when the query
 * already HAS data. A first read with none is reused as the "refresh", and it
 * answers with what the server held before the save. On a page just opened,
 * a quick save was therefore lost from view: the visa dialog said "No visa
 * yet", the CEO's decisions card said "No decisions yet", both with the row
 * saved. Fixing each screen (`refreshAfterWrite`) left every other screen
 * with the same race, so the client does it for every invalidate: a first
 * read still in flight is cancelled, then the invalidate starts a fresh one.
 * A read that already has data is left to TanStack, which cancels it itself.
 */
export class PosQueryClient extends QueryClient {
  override async invalidateQueries<TTaggedQueryKey extends readonly unknown[] = readonly unknown[]>(
    filters?: InvalidateQueryFilters<TTaggedQueryKey>,
    options?: InvalidateOptions,
  ): Promise<void> {
    await this.cancelQueries({
      ...(filters?.queryKey ? { queryKey: filters.queryKey, exact: filters.exact } : {}),
      predicate: (q) =>
        q.state.data === undefined &&
        q.state.fetchStatus === 'fetching' &&
        (filters?.predicate ? filters.predicate(q) : true),
    });
    return super.invalidateQueries(filters, options);
  }
}

/**
 * Convenience constructor — apps that need to override defaults should pass
 * a partial config; tests should construct their own ad-hoc client with
 * `retry: false` to keep error surfacing fast.
 */
export function createPosQueryClient(overrides?: QueryClientConfig): QueryClient {
  if (!overrides) return recheckSessionOnUnauthorized(new PosQueryClient(defaultQueryClientConfig));
  const client = new PosQueryClient({
    ...defaultQueryClientConfig,
    ...overrides,
    defaultOptions: {
      ...defaultQueryClientConfig.defaultOptions,
      ...overrides.defaultOptions,
      queries: {
        ...defaultQueryClientConfig.defaultOptions?.queries,
        ...overrides.defaultOptions?.queries,
      },
      mutations: {
        ...defaultQueryClientConfig.defaultOptions?.mutations,
        ...overrides.defaultOptions?.mutations,
      },
    },
  });
  return recheckSessionOnUnauthorized(client);
}
